import * as React from "react";
import { cn } from "@/lib/utils";
import { ButtonIcon } from "@/components/button/buttonIcon/buttonIcon";
import { Icon } from "@/components/icon/icon";
import styles from "./paginationArrows.module.css";

// Slightly under ButtonIcon's 24px default so the icons sit comfortably in the
// 32px ghost button box. lucide sizes ArrowLeft/Right and CirclePause/Play on
// the same grid, so one value reads balanced across all three.
const ICON_SIZE = 20;

export interface PaginationArrowsProps {
  /** Go to the previous slide. */
  onPrev: () => void;
  /** Go to the next slide. */
  onNext: () => void;
  /** Whether autoplay is currently running (controls the middle icon). */
  isPlaying: boolean;
  /** Toggle autoplay play/pause. */
  onPlayPause: () => void;
  className?: string;
  /** aria-label for the previous arrow. */
  prevLabel?: string;
  /** aria-label for the next arrow. */
  nextLabel?: string;
}

/**
 * Carousel control row: prev arrow · play/pause · next arrow. All three are
 * ghost icon buttons (transparent, accent background on hover). The middle
 * button's icon swaps between a circle-pause (while playing) and circle-play
 * (while paused). The arrows are hidden below 768px (mobile uses swipe); the
 * play/pause button stays visible.
 */
const PaginationArrows = React.forwardRef<HTMLDivElement, PaginationArrowsProps>(
  (
    {
      onPrev,
      onNext,
      isPlaying,
      onPlayPause,
      className,
      prevLabel = "Previous",
      nextLabel = "Next",
    },
    ref,
  ) => {
    return (
      <div ref={ref} className={cn(styles.root, className)}>
        <ButtonIcon
          className={styles.arrow}
          aria-label={prevLabel}
          onClick={onPrev}
        >
          <Icon name="ArrowLeft" size={ICON_SIZE} />
        </ButtonIcon>
        <ButtonIcon
          aria-label={isPlaying ? "Pause autoplay" : "Play autoplay"}
          onClick={onPlayPause}
        >
          <Icon name={isPlaying ? "CirclePause" : "CirclePlay"} size={ICON_SIZE} />
        </ButtonIcon>
        <ButtonIcon
          className={styles.arrow}
          aria-label={nextLabel}
          onClick={onNext}
        >
          <Icon name="ArrowRight" size={ICON_SIZE} />
        </ButtonIcon>
      </div>
    );
  },
);
PaginationArrows.displayName = "PaginationArrows";

export { PaginationArrows };
